import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Field, Input } from '@w7/ui'
import {
  clearAllAuth,
  isDemoSession,
  rememberNext,
  resolveAuthPhase,
  startDemoSession,
} from '@/auth/oidc'
import { bookmakerApi, bmErrorMessage } from '@/api/bookmaker'
import { clearLabSession, setLabSession } from '@/config/lab'
import { flashToast, toastCopy, useAppToast } from '@/ui/toaster'
import { STRIX_LOGO_SRC } from './StrixMark'
import './auth.css'

/**
 * Login layout matches Session-UI (`sm-login` card).
 * Lab Bookmaker: password → optional Google Authenticator OTP.
 */

function unwrapLoginPayload(result: unknown): Record<string, unknown> {
  const row = (result && typeof result === 'object' ? result : {}) as Record<string, unknown>
  if (typeof row.accessToken === 'string' || row.is2faEnabled === true) return row
  if (row.data && typeof row.data === 'object') return row.data as Record<string, unknown>
  return row
}

export function LoginPage() {
  const navigate = useNavigate()
  const toast = useAppToast()
  const [params] = useSearchParams()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [pending2faId, setPending2faId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const next = params.get('next') || '/InPlay'
  const reason = params.get('reason')
  const needsOtp = Boolean(pending2faId)

  useEffect(() => {
    const phase = resolveAuthPhase()
    if (phase.phase === 'ready') navigate(next.startsWith('/') ? next : '/', { replace: true })
    if (phase.phase === 'no-access') navigate('/no-access', { replace: true })
  }, [navigate, next])

  useEffect(() => {
    if (reason === 'expired') setFormError('Session expired. Sign in again.')
    else if (reason === 'denied') setFormError('Sign-in was cancelled.')
    else if (reason === 'error') setFormError('Sign-in failed. Try again.')
  }, [reason])

  const completeLogin = (payload: Record<string, unknown>, fallbackUsername: string) => {
    const token = typeof payload.accessToken === 'string' ? payload.accessToken : ''
    if (!token) {
      setFormError('Login failed — no access token returned.')
      setBusy(false)
      return
    }

    clearAllAuth()
    clearLabSession()
    setLabSession({
      accessToken: token,
      _id: payload._id != null ? String(payload._id) : undefined,
      username: payload.username != null ? String(payload.username) : fallbackUsername,
      level: payload.level as string | number | undefined,
      is2faEnabled: Boolean(payload.is2faEnabled),
    })
    startDemoSession()
    rememberNext(next)
    flashToast(toastCopy.loginSuccess)
    navigate(next.startsWith('/') ? next : '/', { replace: true })
  }

  const onSubmitPassword = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (!username.trim() || !password) {
      setFormError('Enter username and password.')
      return
    }

    setBusy(true)
    try {
      const result = await bookmakerApi.userLogin({
        userName: username.trim(),
        password,
      })
      const payload = unwrapLoginPayload(result)

      if (payload.is2faEnabled && payload._id != null && !payload.accessToken) {
        setPending2faId(String(payload._id))
        setOtp('')
        setBusy(false)
        return
      }

      completeLogin(payload, username.trim())
    } catch (ex) {
      setFormError(bmErrorMessage(ex))
      toast.error('Login failed', bmErrorMessage(ex))
      setBusy(false)
    }
  }

  const onSubmitOtp = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)
    const code = otp.replace(/\s/g, '')
    if (!pending2faId || code.length !== 6) {
      setFormError('Enter the 6-digit code from Google Authenticator.')
      return
    }

    setBusy(true)
    try {
      const result = await bookmakerApi.verifySecreteKeyForLogin({
        _id: pending2faId,
        token: code,
      })
      const payload = unwrapLoginPayload(result)
      if (payload.value === false || (payload.data === false && !payload.accessToken)) {
        const msg =
          typeof payload.msg === 'string' && payload.msg
            ? payload.msg
            : 'Invalid authenticator code.'
        setFormError(msg)
        toast.error('2FA failed', msg)
        setBusy(false)
        return
      }
      completeLogin(payload, username.trim())
    } catch (ex) {
      setFormError(bmErrorMessage(ex))
      toast.error('2FA failed', bmErrorMessage(ex))
      setBusy(false)
    }
  }

  const backToPassword = () => {
    setPending2faId(null)
    setOtp('')
    setFormError(null)
  }

  if (isDemoSession() && resolveAuthPhase().phase === 'ready') {
    return <Navigate to={next.startsWith('/') ? next : '/'} replace />
  }

  return (
    <div className="sm-login" data-testid="login-screen">
      <div className="sm-login-bg" aria-hidden="true" />
      <div className="sm-login-card">
        <div className="sm-login-brand">
          <img src={STRIX_LOGO_SRC} alt="" width={40} height={40} decoding="async" />
          <div>
            <strong>Book Maker</strong>
            <span>Ops console</span>
          </div>
        </div>
        <h1>{needsOtp ? 'Authenticator' : 'Sign in'}</h1>
        <p className="sm-login-lede">
          {needsOtp
            ? 'Enter the 6-digit code from Google Authenticator'
            : 'Enter your credentials to continue'}
        </p>

        {formError ? (
          <p className="sm-login-alert" role="alert">
            {formError}
          </p>
        ) : null}

        {needsOtp ? (
          <form className="sm-login-form" onSubmit={(e) => void onSubmitOtp(e)} noValidate>
            <Field label="Authentication code" required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  name="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoFocus
                  value={otp}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  disabled={busy}
                  placeholder="6-digit code"
                  onChange={(ev) => {
                    setOtp(ev.target.value.replace(/\D/g, '').slice(0, 6))
                    if (formError) setFormError(null)
                  }}
                />
              )}
            </Field>

            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={busy || otp.replace(/\s/g, '').length !== 6}
              data-testid="login-otp-submit"
            >
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={backToPassword}>
              Back
            </Button>
          </form>
        ) : (
          <form className="sm-login-form" onSubmit={(e) => void onSubmitPassword(e)} noValidate>
            <Field label="Username" required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  name="username"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  value={username}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  disabled={busy}
                  placeholder="Username"
                  onChange={(ev) => {
                    setUsername(ev.target.value)
                    if (formError) setFormError(null)
                  }}
                />
              )}
            </Field>

            <Field label="Password" required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  disabled={busy}
                  placeholder="Password"
                  onChange={(ev) => {
                    setPassword(ev.target.value)
                    if (formError) setFormError(null)
                  }}
                />
              )}
            </Field>

            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={busy}
              data-testid="login-submit"
            >
              {busy ? 'Signing in…' : 'Login'}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
